import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Avatar,
  AppBar,
  Toolbar,
  IconButton,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Card,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Logout,
  DateRange,
  CloudUpload,
  Delete,
  Send,
  ArrowBack,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enAU } from 'date-fns/locale';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useDropzone } from 'react-dropzone';
import toast from 'react-hot-toast';
import { getLRApprovers, getGraphClient, testGraphPermissions } from '../services/graphService';
import { createRequest } from '../services/dataServiceWrapper';
import { uploadFiles } from '../services/fileService';
import { sendApproverNotification, sendSubmitterConfirmation } from '../services/emailService';
import { createNotification, NotificationType } from '../services/notificationService';
import { differenceInDays, differenceInHours } from 'date-fns';

const schema = yup.object({
  leaveType: yup.string().required('Leave type is required'),
  manager: yup.string().email('Invalid email').required('Manager is required'),
  firstDay: yup.date().required('First day of leave is required').nullable(),
  lastDay: yup.date()
    .required('Last day of leave is required')
    .nullable()
    .test('is-after-first', 'Last day must be after first day', function(value) {
      const { firstDay } = this.parent;
      if (!firstDay || !value) return true;
      return value >= firstDay;
    }),
  notes: yup.string(),
});

const leaveTypes = [
  'Annual Leave',
  'Sick Leave',
  'Personal Leave',
  'Compassionate Leave',
  'Parental Leave',
  'Long Service Leave',
  'Leave Without Pay',
  'Study Leave',
  'Other'
];

const LeaveRequest = () => {
  const { accounts, instance } = useMsal();
  const navigate = useNavigate();
  const account = accounts[0];
  const [managers, setManagers] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [daysHoursText, setDaysHoursText] = useState('');
  const [isManualOverride, setIsManualOverride] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      leaveType: '',
      manager: '',
      firstDay: null,
      lastDay: null,
      notes: '',
    },
  });

  const firstDay = watch('firstDay');
  const lastDay = watch('lastDay');

  useEffect(() => {
    loadManagers();
  }, [account]);

  useEffect(() => {
    // Only auto-calculate if not manually overridden
    if (!isManualOverride && firstDay && lastDay) {
      if (lastDay >= firstDay) {
        // Add 1 to include both start and end days (e.g., Mon-Fri = 5 days, not 4)
        const totalDays = differenceInDays(lastDay, firstDay) + 1;
        const totalHours = differenceInHours(lastDay, firstDay);
        const remainingHours = totalHours % 24;
        setDaysHoursText(`${totalDays} Days / ${remainingHours} Hours`);
      } else {
        setDaysHoursText('Invalid Date Range');
      }
    } else if (!isManualOverride) {
      setDaysHoursText('');
    }
  }, [firstDay, lastDay, isManualOverride]);

  const loadManagers = async () => {
    try {
      const graphClient = getGraphClient(instance, account);

      // Test permissions first
      console.log('Testing Graph API permissions...');
      await testGraphPermissions(graphClient);

      const approvers = await getLRApprovers(graphClient);
      setManagers(approvers);
    } catch (error) {
      console.error('Error loading managers:', error);
      toast.error('Failed to load managers list');
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles) => {
      setFiles((prev) => [...prev, ...acceptedFiles]);
    },
    accept: {
      'application/pdf': ['.pdf'],
      'image/*': ['.png', '.jpg', '.jpeg'],
      'application/msword': ['.doc', '.docx'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    },
  });

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const userEmail = account?.email || account?.username || 'current.user@acom.edu.au';
      const userName = account?.name || userEmail.split('@')[0];

      const leaveRequest = {
        id: Date.now().toString(),
        type: 'leave',
        title: `Leave Request - ${data.leaveType}`,
        submittedBy: userEmail,
        submitterName: userName,
        submittedDate: new Date().toISOString(),
        status: 'pending',
        approver: data.manager,
        
        // Leave-specific fields
        leaveType: data.leaveType,
        firstDayOfLeave: data.firstDay.toISOString(),
        lastDayOfLeave: data.lastDay.toISOString(),
        daysRequested: differenceInDays(data.lastDay, data.firstDay) + 1, // Include both start and end days
        daysHoursText: daysHoursText,
        notes: data.notes || '',

        // File attachments - empty, will be uploaded separately
        documents: [],
      };

      const createdRequest = await createRequest(leaveRequest);

      // Upload files if any (this will create entries in request_documents table)
      if (files.length > 0) {
        try {
          await uploadFiles(createdRequest.id, files, userEmail);
        } catch (uploadError) {
          console.error('File upload failed:', uploadError);
          // Continue anyway - request is created
        }
      }

      // Send email notification to manager
      await sendApproverNotification(leaveRequest, data.manager);

      // Send confirmation email to submitter
      await sendSubmitterConfirmation(leaveRequest);

      // Create notification for manager
      createNotification({
        type: NotificationType.APPROVAL_NEEDED,
        userEmail: data.manager,
        recipientEmail: data.manager,
        title: 'New Leave Request',
        message: `${userName} has submitted a leave request for ${data.leaveType}`,
        requestId: leaveRequest.id
      });

      toast.success('Leave request submitted successfully!');
      navigate('/my-requests');
    } catch (error) {
      console.error('Error submitting leave request:', error);
      toast.error('Failed to submit leave request');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    instance.logoutPopup({
      postLogoutRedirectUri: '/',
    });
    navigate('/login');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* Header */}
      <AppBar position="static" sx={{ bgcolor: '#0078d4' }}>
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <img
              src="/acom-logo.png"
              alt="ACOM Logo"
              style={{ 
                height: '40px', 
                marginRight: '16px',
                objectFit: 'contain',
                borderRadius: '8px',
                padding: '4px',
                backgroundColor: 'white'
              }}
            />
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Apps Portal
            </Typography>
          </Box>

          <Button
            color="inherit"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/dashboard')}
            sx={{ mr: 2 }}
          >
            Back to Dashboard
          </Button>

          <Button
            color="inherit"
            startIcon={<DateRange />}
            onClick={() => navigate('/my-requests')}
            sx={{ mr: 2 }}
          >
            My Requests
          </Button>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body1">
              {account?.name || account?.username}
            </Typography>
            <Avatar sx={{ bgcolor: '#1976d2' }}>
              {(account?.name || account?.username || 'U')[0].toUpperCase()}
            </Avatar>
            <IconButton color="inherit" onClick={handleLogout}>
              <Logout />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Form Content */}
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 4 }}>
          <Typography variant="h4" gutterBottom sx={{ color: '#0078d4', fontWeight: 'bold', mb: 3 }}>
            Leave Request Form
          </Typography>

          <Typography variant="body1" paragraph sx={{ mb: 4 }}>
            Hello! We understand that everyone needs a break or may have personal obligations. That's why we've made this simple Leave 
            Request Form for you. Please fill in the necessary details about your planned absence, so we can ensure it's managed effectively 
            and your work is covered during this period. Your request will be processed according to ACOM's leave policy. Should there be 
            any questions or additional information needed, your manager or the HR department will get in touch with you. Thank you for 
            keeping us informed!
          </Typography>

          <form onSubmit={handleSubmit(onSubmit)}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Leave Type and Manager Row */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
                <Card sx={{ p: 3, bgcolor: '#f8f9fa' }}>
                  <FormControl fullWidth error={!!errors.leaveType}>
                    <InputLabel>Leave Type</InputLabel>
                    <Controller
                      name="leaveType"
                      control={control}
                      render={({ field }) => (
                        <Select {...field} label="Leave Type">
                          {leaveTypes.map((type) => (
                            <MenuItem key={type} value={type}>
                              {type}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                    {errors.leaveType && (
                      <Typography variant="caption" color="error">
                        {errors.leaveType.message}
                      </Typography>
                    )}
                  </FormControl>
                </Card>

                <Card sx={{ p: 3, bgcolor: '#f8f9fa' }}>
                  <FormControl fullWidth error={!!errors.manager}>
                    <InputLabel>Manager</InputLabel>
                    <Controller
                      name="manager"
                      control={control}
                      render={({ field }) => (
                        <Select {...field} label="Manager">
                          {managers.map((manager) => (
                            <MenuItem key={manager.mail || manager.email} value={manager.mail || manager.email}>
                              {manager.displayName || manager.name} ({manager.mail || manager.email})
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                    {errors.manager && (
                      <Typography variant="caption" color="error">
                        {errors.manager.message}
                      </Typography>
                    )}
                  </FormControl>
                </Card>
              </Box>

              {/* Date Selection Row */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 3 }}>
                <Card sx={{ p: 3, bgcolor: '#f8f9fa' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    First day of leave
                  </Typography>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enAU}>
                    <Controller
                      name="firstDay"
                      control={control}
                      render={({ field }) => (
                        <DatePicker
                          {...field}
                          format="dd/MM/yyyy"
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              error: !!errors.firstDay,
                              helperText: errors.firstDay?.message,
                              placeholder: "DD/MM/YYYY",
                            },
                          }}
                        />
                      )}
                    />
                  </LocalizationProvider>
                </Card>

                <Card sx={{ p: 3, bgcolor: '#f8f9fa' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Last day of leave
                  </Typography>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enAU}>
                    <Controller
                      name="lastDay"
                      control={control}
                      render={({ field }) => (
                        <DatePicker
                          {...field}
                          format="dd/MM/yyyy"
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              error: !!errors.lastDay,
                              helperText: errors.lastDay?.message,
                              placeholder: "DD/MM/YYYY",
                            },
                          }}
                        />
                      )}
                    />
                  </LocalizationProvider>
                </Card>

                <Card sx={{ p: 3, bgcolor: '#f8f9fa' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Days / Hours
                  </Typography>
                  <TextField
                    fullWidth
                    value={daysHoursText}
                    onChange={(e) => {
                      setDaysHoursText(e.target.value);
                      setIsManualOverride(true);
                    }}
                    onFocus={() => setIsManualOverride(true)}
                    onBlur={() => {
                      // If user clears the field, reset to auto-calculate
                      if (!daysHoursText) {
                        setIsManualOverride(false);
                      }
                    }}
                    placeholder="e.g., 5 Days / 0 Hours"
                    sx={{ mt: 1 }}
                    helperText="Auto-calculated or enter custom value"
                  />
                </Card>
              </Box>

              {/* Upload Files and Notes Row */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
                <Card sx={{ p: 3, bgcolor: '#f8f9fa' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Upload Files
                  </Typography>
                  <Box
                    {...getRootProps()}
                    sx={{
                      border: '2px dashed #ccc',
                      borderRadius: 1,
                      p: 3,
                      textAlign: 'center',
                      cursor: 'pointer',
                      bgcolor: isDragActive ? 'action.hover' : 'transparent',
                      '&:hover': {
                        bgcolor: 'action.hover',
                      },
                      minHeight: '150px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                    }}
                  >
                    <input {...getInputProps()} />
                    {files.length === 0 ? (
                      <>
                        <Typography>There is nothing attached.</Typography>
                        <Button
                          startIcon={<CloudUpload />}
                          sx={{ mt: 1 }}
                          variant="text"
                        >
                          Attach file
                        </Button>
                      </>
                    ) : (
                      <Box>
                        {files.map((file, index) => (
                          <Box
                            key={index}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              mb: 1,
                            }}
                          >
                            <Typography variant="body2">{file.name}</Typography>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                removeFile(index);
                              }}
                            >
                              <Delete />
                            </IconButton>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                </Card>

                <Card sx={{ p: 3, bgcolor: '#f8f9fa' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Notes / Comments
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={5}
                    {...register('notes')}
                    placeholder="Add any additional notes or comments..."
                  />
                </Card>
              </Box>

              {/* Submit Button */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <Send />}
                  sx={{
                    px: 6,
                    py: 1.5,
                    bgcolor: '#0078d4',
                    '&:hover': {
                      bgcolor: '#106ebe',
                    },
                  }}
                >
                  Submit
                </Button>
              </Box>
            </Box>
          </form>
        </Paper>
      </Container>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          py: 3,
          px: 2,
          mt: 'auto',
          backgroundColor: 'background.paper',
          textAlign: 'center',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          © 2024 ACOM Apps Portal. All rights reserved.
        </Typography>
      </Box>
    </Box>
  );
};

export default LeaveRequest;